import React, { useEffect, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers';
import './style.css';

const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item) return '#';
  const url = item['@id'] || '#';
  const isFile = item['@type'] === 'File';
  return isFile ? `${url}/@@download/file` : url;
};

const extractTOCEntriesInSection = (blockId, blocks, layout, data = {}) => {
  const allowH2 = data.useH2 ?? true;
  const allowH3 = data.useH3 ?? true;
  const allowedTypes = [
    ...(allowH2 ? ['heading-two', 'h2'] : []),
    ...(allowH3 ? ['heading-three', 'h3'] : []),
  ];

  const startIndex = layout.indexOf(blockId);
  const nextSameBlock = layout.slice(startIndex + 1).find((id) => blocks[id]?.['@type'] === 'buttonRow');
  const endIndex = nextSameBlock ? layout.indexOf(nextSameBlock) : layout.length;
  const targetIds = layout.slice(startIndex + 1, endIndex);

  const entries = [];

  targetIds.forEach((id) => {
    const block = blocks[id];
    if ((block?.['@type'] === 'text' || block?.['@type'] === 'slate') && Array.isArray(block.value)) {
      block.value.forEach((node) => {
        if (allowedTypes.includes(node.type)) {
          const text = node.children?.map((c) => c.text).join('').trim();
          const anchor = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          if (text) entries.push({ id: anchor, title: text });
        }
      });
    }
  });

  return entries;
};

const ButtonRowView = ({ data, properties = {}, block, pathname }) => {
  const [containedItems, setContainedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const image = data.backgroundImage?.[0];
  const alignment = data.alignment || 'center';
  const imageUrl = image?.['@id'] ? `${image['@id']}/@@images/image` : '';
  const backgroundColor = data.backgroundColor || 'var(--lightgrey)';

  const buttonSource = data.buttonSource || 'contained';
  const useContainedItems = buttonSource === 'contained';
  const useTOC = buttonSource === 'toc';

  // Fetch contained items when useContainedItems is enabled
  useEffect(() => {
    if (useContainedItems) {
      setIsLoading(true);
      
      // First, check if items are already in properties
      if (properties?.items && Array.isArray(properties.items)) {
        console.log('Using items from properties:', properties.items);
        const allowedTypes = data.containedItemTypes || ['File', 'Document'];
        const filtered = properties.items.filter((item) => 
          allowedTypes.includes(item['@type'])
        );
        console.log('Filtered items from properties:', filtered);
        setContainedItems(filtered);
        setIsLoading(false);
        return;
      }
      
      // If not in properties, fetch from API
      let fetchUrl = pathname;
      
      // If pathname is not available, try to get it from properties
      if (!fetchUrl && properties?.['@id']) {
        fetchUrl = properties['@id'];
      }
      
      // Flatten the URL to app URL format
      if (fetchUrl) {
        fetchUrl = flattenToAppURL(fetchUrl);
      }
      
      console.log('Fetching contained items from:', fetchUrl);
      
      if (fetchUrl) {
        // Try fetching the page itself with metadata
        fetch(fetchUrl, {
          headers: {
            'Accept': 'application/json',
          },
        })
          .then((res) => {
            console.log('Response status:', res.status);
            console.log('Content-Type:', res.headers.get('content-type'));
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then((responseData) => {
            console.log('Full response data:', responseData);
            console.log('Response keys:', Object.keys(responseData));
            
            // Check multiple possible locations for items
            const items = responseData.items || responseData.member || responseData['hydra:member'] || [];
            console.log('Items found:', items);
            console.log('Items count:', items.length);
            
            if (items.length > 0) {
              console.log('First item sample:', items[0]);
            }
            
            const allowedTypes = data.containedItemTypes || ['File', 'Document'];
            console.log('Allowed types:', allowedTypes);
            
            const filtered = items.filter((item) => {
              console.log('Item type:', item['@type'], 'Title:', item.title);
              return allowedTypes.includes(item['@type']);
            });
            
            console.log('Filtered items:', filtered.length);
            setContainedItems(filtered);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Error fetching contained items:', err);
            setContainedItems([]);
            setIsLoading(false);
          });
      } else {
        console.error('No pathname available for fetching contained items');
        setContainedItems([]);
        setIsLoading(false);
      }
    } else {
      setContainedItems([]);
      setIsLoading(false);
    }
  }, [useContainedItems, pathname, properties, data.containedItemTypes]);

  const colorLabelMap = {
    '#000000': 'black',
    '#ffffff': 'white',
    '#f5f5f5': 'light-grey',
    '#ffcc00': 'yellow',
    '#006699': 'blue',
  };

  const colorLabel = (colorLabelMap[backgroundColor] || backgroundColor).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  const noBg = !imageUrl;
  const darkButtons = noBg && ['#ffffff', '#f5f5f5', '#ffcc00', 'var(--lightgrey)'].includes(backgroundColor);

  const wrapperStyle = {
    backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
    backgroundColor,
  };

  const wrapperClass = `button-row-block ${data.isFullWidth ? 'full-width' : ''} ${noBg ? 'no-bg' : ''} ${darkButtons ? 'dark-buttons' : ''} color-${colorLabel}`;

  const layout = properties.blocks_layout?.items || [];
  const blocks = properties.blocks || {};
  const tocEntries = useTOC ? extractTOCEntriesInSection(block, blocks, layout, data) : [];

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      {imageUrl && <div className="overlay" />}
      <div className={`button-row-inner align-${alignment}`}>
        <div className="button-row-content">
          {data.title && <h1>{data.title}</h1>}
          {data.text && <p>{data.text}</p>}
        </div>

        {useTOC ? (
          <div className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}>
            {tocEntries.map((entry, i) => (
              <a key={i} href={`#${entry.id}`} className="row-button">
                {entry.title}
              </a>
            ))}
          </div>
        ) : useContainedItems ? (
          <div className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}>
            {isLoading ? (
              <div className="row-button" style={{ opacity: 0.6 }}>Loading...</div>
            ) : containedItems.length > 0 ? (
              containedItems.map((item, i) => {
                const href = item['@type'] === 'File' ? `${item['@id']}/@@download/file` : item['@id'];
                const isExternal = href?.startsWith('http');
                return (
                  <a
                    key={i}
                    href={href}
                    className="row-button"
                    {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {item.title}
                  </a>
                );
              })
            ) : (
              <div className="row-button" style={{ opacity: 0.6 }}>No items found</div>
            )}
          </div>
        ) : buttonSource === 'custom' && data.buttons?.length > 0 ? (
          <div className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}>
            {data.buttons.map((btn, i) => {
              const href = getHref(btn.link);
              const isExternal = href?.startsWith('http');
              return (
                <a
                  key={i}
                  href={href}
                  className="row-button"
                  {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {btn.label}
                </a>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ButtonRowView;